import { Controller } from "@hotwired/stimulus"

// Handles: drop zone for multiple files, upload progress, drag-and-drop reorder
export default class extends Controller {
  static targets = ["dropZone", "previewList", "fileInput", "progressBar", "progressText", "videoUrlList", "videoUrlTemplate", "addVideoBtn", "dropZoneWrapper", "limitMessage"]
  static values = {
    maxFiles: { type: Number, default: 7 },
    accept: { type: String, default: "image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime" },
    nextPosition: { type: Number, default: 0 },
    limitReachedMessage: { type: String, default: "Limit reached (%{current}/%{max}). Remove existing to add new." }
  }

  connect() {
    this.pendingFiles = []
    this.nextIndex = this._getNextIndex()
    this.videoUrlCount = this.videoUrlListTarget?.querySelectorAll(".input-group").length || 0
    this._setupDropZone()
    this._setupFileInput()
    this._setupSortable()
    this._setupFormSubmit()
    this._setupVideoUrls()
  }

  removeItem(event) {
    const btn = event.currentTarget
    const item = btn.closest(".marketplace-media-item")
    if (item) this._removeItem(item)
  }

  removeVideoUrl(event) {
    const group = event.currentTarget.closest(".input-group")
    if (group) group.remove()
  }

  _getNextIndex() {
    const inputs = this.element.querySelectorAll('[name*="[marketplace_listing_media_attributes]"]')
    let max = -1
    inputs.forEach(inp => {
      const m = inp.name.match(/\[(\d+)\]/)
      if (m) max = Math.max(max, parseInt(m[1], 10))
    })
    return max + 1
  }

  _setupDropZone() {
    const target = this.hasDropZoneWrapperTarget ? this.dropZoneWrapperTarget : this.dropZoneTarget
    if (!target) return
    ;["dragenter", "dragover", "dragleave", "drop"].forEach(ev => {
      target.addEventListener(ev, this._handleDrag.bind(this), false)
    })
  }

  _handleDrag(e) {
    e.preventDefault()
    e.stopPropagation()
    if (this._isAtLimit()) return
    if (this.hasDropZoneTarget) {
      if (e.type === "dragenter" || e.type === "dragover") {
        this.dropZoneTarget.classList.add("marketplace-media-dropzone--active")
      } else if (e.type === "dragleave" || e.type === "drop") {
        this.dropZoneTarget.classList.remove("marketplace-media-dropzone--active")
      }
    }
    if (e.type === "drop") {
      const files = Array.from(e.dataTransfer.files).filter(f =>
        f.type.startsWith("image/") || f.type.startsWith("video/")
      )
      this._addFiles(files)
    }
  }

  _setupFileInput() {
    if (!this.hasFileInputTarget) return
    this.fileInputTarget.addEventListener("change", (e) => {
      if (this._isAtLimit()) {
        e.target.value = ""
        return
      }
      const files = Array.from(e.target.files || [])
      this._addFiles(files)
      e.target.value = ""
    })
  }

  _isAtLimit() {
    return this._countMedia() >= this.maxFilesValue
  }

  _updateLimitState() {
    if (!this.hasDropZoneTarget || !this.hasLimitMessageTarget) return
    const atLimit = this._isAtLimit()
    const current = this._countMedia()
    const max = this.maxFilesValue
    if (atLimit) {
      this.dropZoneTarget.classList.add("d-none")
      this.dropZoneTarget.removeAttribute("for")
      this.limitMessageTarget.classList.remove("d-none")
      const textEl = this.limitMessageTarget.querySelector("[data-marketplace-media-upload-target='limitMessageText']")
      if (textEl) {
        textEl.textContent = this.limitReachedMessageValue
          .replace("%{current}", current)
          .replace("%{max}", max)
      }
    } else {
      this.dropZoneTarget.classList.remove("d-none")
      this.dropZoneTarget.setAttribute("for", "marketplace-media-file-input")
      this.limitMessageTarget.classList.add("d-none")
    }
  }

  _addFiles(files) {
    const current = this._countMedia()
    const remaining = Math.max(0, this.maxFilesValue - current)
    const toAdd = files.slice(0, remaining)
    if (toAdd.length < files.length) {
      console.warn(`Limit: max ${this.maxFilesValue} files. Added ${toAdd.length}, skipped ${files.length - toAdd.length}`)
    }
    toAdd.forEach(file => this._addFile(file))
  }

  _countMedia() {
    // Only file-based media (images + video files) count toward limit, not video URLs
    const items = this.previewListTarget?.querySelectorAll(".marketplace-media-item:not(.marketplace-media-item--removed)") || []
    return items.length
  }

  _addFile(file) {
    if (!this.hasPreviewListTarget) return
    const idx = this.nextIndex++
    const item = document.createElement("div")
    item.className = "marketplace-media-item"
    item.dataset.mediaIndex = idx
    item.dataset.isNew = "true"
    item.draggable = true

    const preview = document.createElement("div")
    preview.className = "marketplace-media-preview"
    if (file.type.startsWith("image/")) {
      const img = document.createElement("img")
      img.src = URL.createObjectURL(file)
      img.alt = file.name
      img.draggable = false
      preview.appendChild(img)
    } else {
      const icon = document.createElement("span")
      icon.className = "marketplace-media-video-icon"
      icon.innerHTML = '<i class="fa-solid fa-video"></i>'
      preview.appendChild(icon)
      const name = document.createElement("span")
      name.className = "marketplace-media-filename"
      name.textContent = file.name
      preview.appendChild(name)
    }

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "marketplace-media-remove"
    removeBtn.innerHTML = '<i class="fa-solid fa-times"></i>'
    removeBtn.addEventListener("click", () => this._removeItem(item))

    item.appendChild(preview)
    item.appendChild(removeBtn)

    const form = this.element.closest("form")
    const fileInput = document.createElement("input")
    fileInput.type = "file"
    fileInput.name = `marketplace_listing[marketplace_listing_media_attributes][${idx}][file]`
    fileInput.style.display = "none"
    const dt = new DataTransfer()
    dt.items.add(file)
    fileInput.files = dt.files

    const mediaTypeInput = document.createElement("input")
    mediaTypeInput.type = "hidden"
    mediaTypeInput.name = `marketplace_listing[marketplace_listing_media_attributes][${idx}][media_type]`
    mediaTypeInput.value = file.type.startsWith("image/") ? "image" : "video"

    const positionInput = document.createElement("input")
    positionInput.type = "hidden"
    positionInput.name = `marketplace_listing[marketplace_listing_media_attributes][${idx}][position]`
    positionInput.value = this.previewListTarget.children.length
    positionInput.dataset.positionField = "true"

    item.appendChild(fileInput)
    item.appendChild(mediaTypeInput)
    item.appendChild(positionInput)

    this.previewListTarget.appendChild(item)
    this.pendingFiles.push({ file, item })
    this._updatePositions()
    this._updateLimitState()
  }

  _removeItem(item) {
    if (item.dataset.isNew === "true") {
      item.remove()
    } else {
      const destroyInput = item.querySelector(".marketplace-media-destroy-input, input[name*='_destroy']")
      if (destroyInput) destroyInput.value = "1"
      item.classList.add("marketplace-media-item--removed")
      item.style.display = "none"
    }
    this._updatePositions()
    this._updateLimitState()
  }

  _updatePositions() {
    const items = this.previewListTarget?.querySelectorAll(".marketplace-media-item:not(.marketplace-media-item--removed)") || []
    items.forEach((item, i) => {
      const posInput = item.querySelector("[data-position-field]")
      if (posInput) posInput.value = i
    })
  }

  _setupSortable() {
    if (!this.hasPreviewListTarget) return
    const list = this.previewListTarget
    let placeholder = null

    const getSortableItems = () =>
      Array.from(list.children).filter(
        el => el.classList.contains("marketplace-media-item") &&
          !el.classList.contains("marketplace-media-item--removed") &&
          !el.classList.contains("marketplace-media-dragging") &&
          el !== placeholder
      )

    const updatePlaceholderPosition = (clientX) => {
      const items = getSortableItems()
      if (items.length === 0) {
        list.appendChild(placeholder)
        return
      }
      let idx = 0
      for (let i = 0; i < items.length; i++) {
        const rect = items[i].getBoundingClientRect()
        const mid = rect.left + rect.width / 2
        if (clientX < mid) break
        idx = i + 1
      }
      const ref = items[idx]
      list.insertBefore(placeholder, ref || null)
    }

    list.addEventListener("dragstart", (e) => {
      const item = e.target.closest(".marketplace-media-item")
      if (item && !e.target.closest(".marketplace-media-remove")) {
        e.dataTransfer.effectAllowed = "move"
        e.dataTransfer.setData("text/plain", item.dataset.mediaIndex || "")
        item.classList.add("marketplace-media-dragging")
        placeholder = document.createElement("div")
        placeholder.className = "marketplace-media-placeholder"
        placeholder.setAttribute("aria-hidden", "true")
        list.insertBefore(placeholder, item.nextSibling)
      }
    })

    list.addEventListener("dragend", (e) => {
      const item = e.target.closest(".marketplace-media-item")
      if (item) item.classList.remove("marketplace-media-dragging")
      if (placeholder?.parentNode) placeholder.remove()
      placeholder = null
    })

    list.addEventListener("dragover", (e) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
      if (placeholder) updatePlaceholderPosition(e.clientX)
    })

    list.addEventListener("dragenter", (e) => {
      e.preventDefault()
    })

    list.addEventListener("drop", (e) => {
      e.preventDefault()
      e.stopPropagation()
      const fromItem = this.element.querySelector(".marketplace-media-dragging")
      if (!fromItem || !placeholder?.parentNode) return
      list.insertBefore(fromItem, placeholder)
      placeholder.remove()
      placeholder = null
      fromItem.classList.remove("marketplace-media-dragging")
      this._updatePositions()
    })
  }

  _setupFormSubmit() {
    const form = this.element.closest("form")
    if (!form || !this.hasProgressBarTarget) return
    form.addEventListener("submit", (e) => {
      const fileInputs = form.querySelectorAll('input[type="file"][name*="marketplace_listing_media_attributes"]')
      const hasNewFiles = Array.from(fileInputs).some(inp => inp.files?.length > 0)
      if (hasNewFiles) {
        e.preventDefault()
        this._submitWithProgress(form)
      }
    })
  }

  async _submitWithProgress(form) {
    const progressWrap = this.progressBarTarget
    const progressBar = progressWrap?.querySelector(".progress-bar")
    const progressText = this.progressTextTarget
    const spinner = progressWrap?.querySelector(".spinner-border")

    progressWrap?.classList.remove("d-none")
    progressWrap?.scrollIntoView({ behavior: "smooth", block: "center" })
    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]')
    if (submitBtn) submitBtn.disabled = true
    if (progressBar) {
      progressBar.style.width = "0%"
      progressBar.setAttribute("aria-valuenow", "0")
    }
    if (progressText) progressText.textContent = "0%"
    if (spinner) spinner.classList.remove("d-none")

    const formData = new FormData(form)
    const xhr = new XMLHttpRequest()
    xhr.open(form.method || "POST", form.action)

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrf) {
      xhr.setRequestHeader("X-CSRF-Token", csrf)
    }
    xhr.setRequestHeader("Accept", "text/html")

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100)
          if (progressBar) {
            progressBar.style.width = `${pct}%`
            progressBar.setAttribute("aria-valuenow", pct)
          }
          if (progressText) progressText.textContent = `${pct}%`
          if (spinner) spinner.classList.add("d-none")
        } else if (e.loaded > 0 && progressText) {
          progressText.textContent = this._uploadingLabel()
        }
      })
      xhr.addEventListener("load", () => {
        if (progressBar) {
          progressBar.style.width = "100%"
          progressBar.setAttribute("aria-valuenow", 100)
        }
        if (progressText) progressText.textContent = "100%"
        if (spinner) spinner.classList.add("d-none")
        if (xhr.status >= 200 && xhr.status < 300) {
          const redirectUrl = xhr.responseURL || window.location.href
          if (typeof Turbo !== "undefined") {
            Turbo.visit(redirectUrl, { action: "replace" })
          } else {
            window.location.href = redirectUrl
          }
          resolve()
        } else {
          progressWrap?.classList.add("d-none")
          if (submitBtn) submitBtn.disabled = false
          const parser = new DOMParser()
          const doc = parser.parseFromString(xhr.responseText, "text/html")
          const newBody = doc.querySelector("body")
          if (newBody) {
            document.body.innerHTML = newBody.innerHTML
            document.querySelector(".preloader")?.remove()
            window.dispatchEvent(new Event("turbo:load"))
          }
          reject(new Error(`Upload failed: ${xhr.status}`))
        }
      })
      xhr.addEventListener("error", () => {
        progressWrap?.classList.add("d-none")
        if (submitBtn) submitBtn.disabled = false
        reject(new Error("Upload failed"))
      })
      xhr.send(formData)
    })
  }

  _uploadingLabel() {
    const el = this.progressBarTarget?.closest("[data-uploading-label]") || document.querySelector("[data-uploading-label]")
    return el?.dataset?.uploadingLabel || "Wysyłanie…"
  }

  _setupVideoUrls() {
    if (!this.hasAddVideoBtnTarget || !this.hasVideoUrlListTarget || !this.hasVideoUrlTemplateTarget) return
    this.addVideoBtnTarget.addEventListener("click", (e) => {
      e.preventDefault()
      const template = this.videoUrlTemplateTarget
      const idx = this._getNextIndex()
      this.nextIndex = Math.max(this.nextIndex || 0, idx + 1)
      const pos = this.nextPositionValue
      this.nextPositionValue = pos + 1
      const html = template.innerHTML.replace(/__INDEX__/g, idx).replace(/__POSITION__/g, pos)
      const wrap = document.createElement("div")
      wrap.innerHTML = html
      const newRow = wrap.firstElementChild
      if (newRow) {
        newRow.querySelector("button[data-action]")?.addEventListener("click", (ev) => this.removeVideoUrl(ev))
        this.videoUrlListTarget.appendChild(newRow)
      }
    })
  }
}
